import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Images from "../utils/Images";
import { authApi, flattenErrors, getToken, saveSession } from "../utils/Api";

export default function LoginPage({ intro = false }) {
    const navigate = useNavigate();
    const reduceMotion =
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const [mobileNumber, setMobileNumber] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [revealed, setRevealed] = useState(!intro || reduceMotion);

    useEffect(() => {
        if (getToken()) {
            const timer = setTimeout(
                () => navigate("/home", { replace: true }),
                intro && !reduceMotion ? 1800 : 0
            );
            return () => clearTimeout(timer);
        }

        if (!intro || reduceMotion) return undefined;

        const timer = setTimeout(() => setRevealed(true), 1900);
        return () => clearTimeout(timer);
    }, [intro, navigate, reduceMotion]);

    const handleLogin = async () => {
        setError("");

        if (loading) return;

        if (!/^[0-9]{10}$/.test(mobileNumber)) {
            setError("Please enter a valid 10-digit mobile number.");
            return;
        }

        try {
            setLoading(true);

            const result = await authApi.login(mobileNumber);

            if (!result?.data?.token || !result?.data?.staff) {
                throw new Error("Login did not return a staff session.");
            }

            saveSession(result.data);
            navigate("/home", { replace: true });
        } catch (err) {
            const fieldErrors = flattenErrors(err.errors);
            setError(
                fieldErrors[0] ||
                    err.message ||
                    "Something went wrong. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const showSplash = intro && !revealed;

    return (
        <main className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#18c2dc] via-[#08a8cc] to-[#017fa8] flex items-center justify-center px-4 py-8 font-sans">
            {intro && (
                <>
                    <div className={`absolute -top-32 -left-32 w-96 h-96 rounded-full bg-white/10 blur-3xl pointer-events-none transition-opacity duration-700 ${revealed ? "opacity-0" : "opacity-100 animate-pulse"}`} />
                    <div
                        className={`absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-cyan-200/20 blur-3xl pointer-events-none transition-opacity duration-700 ${revealed ? "opacity-0" : "opacity-100 animate-pulse"}`}
                        style={{ animationDelay: "1s" }}
                    />
                </>
            )}

            <section className="relative z-10 w-full max-w-[420px] flex flex-col items-center">
                <div className={`transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${showSplash ? "mb-0" : "mb-10"}`}>
                    <img
                        src={Images.logo}
                        alt="Crewflow Logo"
                        className={`object-contain max-w-[90vw] transition-[width] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${showSplash ? "w-[320px] splash-logo" : "w-[220px]"}`}
                    />
                </div>

                {intro && (
                    <div className={`splash-status flex flex-col items-center ${revealed ? "is-hidden" : ""}`}>
                        <div className="flex items-center justify-center gap-2.5 splash-dots">
                            {[0, 1, 2].map((index) => (
                                <span
                                    key={index}
                                    className="splash-dot w-2.5 h-2.5 rounded-full bg-white shadow-md"
                                    style={{ animationDelay: `${index * 0.2}s` }}
                                />
                            ))}
                        </div>
                        <p className="mt-5 text-white/70 text-xs font-medium tracking-[0.3em] uppercase splash-text">
                            Getting things ready
                        </p>
                    </div>
                )}

                <div className={`w-full ${intro ? (revealed ? "login-reveal" : "login-hold") : ""}`}>
                    <div className={intro ? "login-panel" : undefined}>
                        <div className="relative w-full pt-[55px]">
                            <img
                                src={Images.subBird}
                                alt="Xpanse Bird"
                                className={`absolute -top-[35px] left-1/2 z-20 -translate-x-1/2 drop-shadow-[0_10px_25px_rgba(0,0,0,0.15)] w-[180px] ${intro && revealed ? "bird-in" : ""}`}
                            />

                            <div className="bg-white rounded-[30px] shadow-[0_26px_70px_rgba(0,45,77,0.22)] px-6 pt-[40px] pb-7">
                                <div className="text-center mb-7">
                                    <h1 className="text-[23px] font-black tracking-tight uppercase text-[#041b3a]">
                                        Cafe Operations
                                    </h1>

                                    <p className="mt-1 text-[12px] leading-none text-[#667085]">
                                        Enter your registered mobile number to get your
                                        outlet checklists.
                                    </p>
                                </div>

                                <label className="block text-[14px] font-bold text-[#39445f] mb-2">
                                    Mobile Number{" "}
                                    <span className="text-red-500">*</span>
                                </label>

                                <div className="flex items-center h-[48px] rounded-[18px] border border-[#d8e8ee] bg-[#fbfdfe] overflow-hidden focus-within:border-[#079dc5] focus-within:shadow-[0_0_0_4px_rgba(7,157,197,0.12)]">
                                    <div className="w-12 h-full flex items-center justify-center text-[#079dc5] text-l border-r border-[#edf3f6]">
                                        ☎
                                    </div>

                                    <input
                                        type="tel"
                                        value={mobileNumber}
                                        maxLength={10}
                                        placeholder="10-digit mobile number"
                                        onChange={(e) => {
                                            const value = e.target.value.replace(
                                                /\D/g,
                                                ""
                                            );
                                            setMobileNumber(value);
                                            setError("");
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                handleLogin();
                                            }
                                        }}
                                        className="flex-1 h-full px-4 text-[17px] outline-none bg-transparent"
                                    />
                                </div>

                                {error && (
                                    <p className="text-[12px] text-red-500 mt-3 font-medium">
                                        {error}
                                    </p>
                                )}

                                <p className="text-[10px] text-[#697386] mt-3">
                                    Access is restricted to authorized outlet staff
                                    only.
                                </p>

                                <button
                                    onClick={handleLogin}
                                    disabled={loading || showSplash}
                                    className="mt-6 w-full h-[58px] rounded-[18px] bg-gradient-to-r from-[#08a9cf] to-[#007ca8] text-white font-bold uppercase shadow-lg hover:-translate-y-[1px] transition disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                                >
                                    {loading ? "Please wait..." : "Continue →"}
                                </button>
                            </div>
                        </div>

                        <div className="mt-5 text-center text-[13px] font-medium text-white/90">
                            CrewFlow · Audit, Compliance & Checklist Management
                        </div>
                    </div>
                </div>
            </section>

            {intro && (
                <style>{`
                    @keyframes logoZoomOut {
                        0% { opacity: 0; transform: scale(3); }
                        15% { opacity: 1; }
                        65% { transform: scale(0.9); }
                        80% { transform: scale(1.05); }
                        100% { opacity: 1; transform: scale(1); }
                    }
                    .splash-logo {
                        animation: logoZoomOut 1.7s cubic-bezier(0.22, 1, 0.36, 1) both;
                        transform-origin: center;
                    }
                    @keyframes dotsAppear {
                        from { opacity: 0; transform: translateY(15px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .splash-dots { animation: dotsAppear 0.6s ease-out 1s both; }
                    @keyframes dotBounce {
                        0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
                        30% { transform: translateY(-9px); opacity: 1; }
                    }
                    .splash-dot { animation: dotBounce 1.2s ease-in-out infinite; }
                    @keyframes textAppear {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .splash-text { animation: textAppear 0.7s ease-out 1.25s both; }
                    .splash-status {
                        max-height: 88px;
                        margin-top: 2.5rem;
                        overflow: hidden;
                        transition: opacity 0.35s ease, max-height 0.5s ease, margin 0.5s ease;
                    }
                    .splash-status.is-hidden {
                        max-height: 0;
                        opacity: 0;
                        margin: 0;
                        pointer-events: none;
                    }
                    .login-hold {
                        display: grid;
                        grid-template-rows: 0fr;
                        opacity: 0;
                        pointer-events: none;
                    }
                    .login-hold > .login-panel {
                        min-height: 0;
                        overflow: hidden;
                    }
                    .login-reveal {
                        display: grid;
                        grid-template-rows: 1fr;
                        opacity: 1;
                        transition: grid-template-rows 0.95s cubic-bezier(0.22, 1, 0.36, 1),
                            opacity 0.7s ease 0.08s;
                    }
                    .login-reveal > .login-panel {
                        min-height: 0;
                        overflow: visible;
                        animation: loginRise 0.95s cubic-bezier(0.22, 1, 0.36, 1) both;
                    }
                    @keyframes loginRise {
                        from { opacity: 0; transform: translateY(42px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .bird-in {
                        animation: birdIn 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.12s both;
                    }
                    @keyframes birdIn {
                        from { opacity: 0; transform: translate(-50%, -28px); }
                        to { opacity: 1; transform: translate(-50%, 0); }
                    }
                    @media (prefers-reduced-motion: reduce) {
                        .splash-logo,
                        .splash-dots,
                        .splash-dot,
                        .splash-text,
                        .login-reveal > .login-panel,
                        .bird-in {
                            animation: none;
                        }
                    }
                `}</style>
            )}
        </main>
    );
}